import "./notification.scss";
import { NotificationService } from "../../Services/notificationService";
import { UserService } from "../../Services/userService";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import avatarImage from "../../Images/person_male.png";
import { useNavigate } from "react-router-dom";
import { getTimeAgo } from "../../Util/timeUtil";
import HomeNotificationVirtualList from "./HomeNotificationVirtualList";
import {
    isCommentStyleNotification,
    isConfidentialShareNotification,
    isHtmlMessage,
    normalizeNotificationNavigatePath,
    NotificationItem,
    resolveNotificationLinkLabelKey,
    resolveNotificationTitle,
} from "./notificationUtils";

const DEFAULT_HOME_NOTIFICATION_PAGE_SIZE = 5;

interface NotificationListProps {
    readNotifications?: NotificationItem[];
    notifications: NotificationItem[];
    navigate: ReturnType<typeof useNavigate>;
    fetchNotificationCount: () => void;
    fetchNotifications: () => void;
    setOpenNotification?: (open: boolean) => void;
    maxDaysAgo?: Date;
    /** When true (e.g. home widget), use virtual scroll (with `previousDays`) or paginator fallback. */
    homeMessages?: boolean;
    /** Page size when `homeMessages` is true without virtual scroll; defaults to {@link DEFAULT_HOME_NOTIFICATION_PAGE_SIZE}. */
    homePageSize?: number;
    /** Loads messages for the last N days via paginated API (enables virtual lazy scroll on home). */
    previousDays?: number;
    /** Increment to reload the virtual list after mark-as-read etc. */
    reloadToken?: number;
    onTotalRecordsChange?: (total: number) => void;
    /** Optimistic read-state updates (e.g. navbar unread list). */
    onNotificationReadChange?: (uuid: string, read: boolean) => void;
    /** When true, show mark-as-unread on read items (full list / home widget). */
    allowMarkUnread?: boolean;
}

interface NotificationRowProps {
    notification: NotificationItem;
    avatar?: string;
    expanded: boolean;
    onToggleExpand: () => void;
    onMarkRead: (uuid: string) => void;
    onMarkUnread?: (uuid: string) => void;
    onNavigate: (path: string) => void;
    translate: (key: string) => string;
}

export const NotificationRow = ({
    notification,
    avatar,
    expanded,
    onToggleExpand,
    onMarkRead,
    onMarkUnread,
    onNavigate,
    translate,
}: NotificationRowProps) => {
    const confidential = isConfidentialShareNotification(notification);
    const commentStyle = isCommentStyleNotification(notification);
    const title = resolveNotificationTitle(notification.title, translate);
    const htmlBody = isHtmlMessage(notification.message);

    return (
        <article
            className={`dashly-notification-item${notification.read ? "" : " dashly-notification-item--unread"}`}
        >
            <div className="dashly-notification-item__icon" aria-hidden>
                {confidential ? (
                    <i className="pi pi-lock" />
                ) : commentStyle && avatar ? (
                    <img src={avatar} alt="" className="dashly-notification-item__avatar" />
                ) : (
                    <i className="pi pi-bell" />
                )}
            </div>

            <div className="dashly-notification-item__body">
                <div className="dashly-notification-item__head">
                    <h3 className="dashly-notification-item__title">{title}</h3>
                    {!notification.read && <span className="dashly-notification-item__dot" aria-label={translate("notification.unreadAria")} />}
                </div>

                {/*{commentStyle && (*/}
                {/*    <p className="dashly-notification-item__meta">*/}
                {/*        <strong>{notification.messageUser?.name || "Dashly"}</strong>{" "}*/}
                {/*        {translate("notification.user.comment")}*/}
                {/*    </p>*/}
                {/*)}*/}

                {htmlBody ? (
                    <div className="dashly-notification-item__html">
                        <div
                            className={`dashly-notification-item__html-inner${expanded ? " is-expanded" : " is-collapsed"}`}
                            dangerouslySetInnerHTML={{ __html: notification.message }}
                        />
                        <button type="button" className="dashly-notification-item__expand" onClick={onToggleExpand}>
                            {expanded ? translate("notification.showLess") : translate("notification.showMore")}
                        </button>
                    </div>
                ) : (
                    <p className="dashly-notification-item__message">{notification.message}</p>
                )}

                <footer className="dashly-notification-item__footer">
                    <span className="dashly-notification-item__time">
                        {notification.createdOnTs
                            ? getTimeAgo(
                                notification.createdOnTs instanceof Date
                                    ? notification.createdOnTs.toISOString()
                                    : String(notification.createdOnTs),
                            )
                            : ""}
                    </span>
                    <div className="dashly-notification-item__actions">
                        {!notification.read && (
                            <button
                                type="button"
                                className="dashly-notification-item__action"
                                title={translate("notification.mark-read")}
                                onClick={() => onMarkRead(notification.uuid)}
                            >
                                <i className="pi pi-check" />
                            </button>
                        )}
                        {notification.read && onMarkUnread && (
                            <button
                                type="button"
                                className="dashly-notification-item__action"
                                title={translate("notification.mark-unread")}
                                onClick={() => onMarkUnread(notification.uuid)}
                            >
                                <i className="pi pi-envelope" />
                            </button>
                        )}
                        {notification.path && (
                            <button
                                type="button"
                                className="dashly-notification-item__action dashly-notification-item__action--primary"
                                title={translate(resolveNotificationLinkLabelKey(notification))}
                                onClick={() => onNavigate(notification.path!)}
                            >
                                <i className="pi pi-arrow-right" />
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        </article>
    );
};

export const NotificationList: React.FC<NotificationListProps> = ({
    notifications,
    navigate,
    fetchNotificationCount,
    fetchNotifications,
    setOpenNotification,
    homeMessages = false,
    homePageSize,
    previousDays,
    reloadToken,
    onTotalRecordsChange,
    onNotificationReadChange,
    allowMarkUnread = false,
}) => {
    if (homeMessages && previousDays != null) {
        return (
            <HomeNotificationVirtualList
                previousDays={previousDays}
                reloadToken={reloadToken}
                onTotalRecordsChange={onTotalRecordsChange}
                allowMarkUnread={allowMarkUnread}
                onNotificationsChange={() => {
                    fetchNotifications();
                    fetchNotificationCount();
                }}
            />
        );
    }

    const [t] = useTranslation();
    const userService = new UserService();
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const avatarsRef = useRef(avatars);
    avatarsRef.current = avatars;
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [first, setFirst] = useState(0);

    const pageRows = useMemo(
        () =>
            homeMessages
                ? Math.max(1, homePageSize ?? DEFAULT_HOME_NOTIFICATION_PAGE_SIZE)
                : notifications.length || 1,
        [homeMessages, homePageSize, notifications.length],
    );

    const visibleNotifications = useMemo(() => {
        if (!homeMessages) {
            return notifications;
        }
        return notifications.slice(first, first + pageRows);
    }, [homeMessages, notifications, first, pageRows]);

    useEffect(() => {
        if (!homeMessages) {
            return;
        }
        setFirst((f) => (f >= notifications.length ? 0 : f));
    }, [notifications.length, homeMessages]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            const uuids = [
                ...new Set(
                    visibleNotifications
                        .map((n) => n.messageUser?.uuid)
                        .filter((uuid): uuid is string => Boolean(uuid)),
                ),
            ].filter((uuid) => !avatarsRef.current[uuid]);
            if (!uuids.length) {
                return;
            }

            const pairs = await Promise.all(
                uuids.map(async (uuid) => {
                    try {
                        return [uuid, await userService.fetchProfilePicture(uuid)] as const;
                    } catch {
                        return [uuid, avatarImage] as const;
                    }
                }),
            );

            if (cancelled) {
                return;
            }

            setAvatars((prev) => {
                const next = { ...prev };
                let changed = false;
                for (const [uuid, url] of pairs) {
                    if (!next[uuid]) {
                        next[uuid] = url;
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [visibleNotifications]);

    const syncNotifications = () => {
        fetchNotificationCount();
        fetchNotifications();
    };

    const markAsRead = (uuid: string) => {
        onNotificationReadChange?.(uuid, true);
        new NotificationService().markRead(uuid).then(syncNotifications).catch(syncNotifications);
    };

    const markAsUnread = (uuid: string) => {
        onNotificationReadChange?.(uuid, false);
        new NotificationService().markUnread(uuid).then(syncNotifications).catch(syncNotifications);
    };

    if (!notifications?.length) {
        return (
            <div className="dashly-notification-empty">
                <i className="pi pi-inbox" aria-hidden />
                <p className="dashly-notification-empty__title">{t("notification.empty.text")}</p>
                <p className="dashly-notification-empty__subtitle">{t("notification.empty.side.text")}</p>
            </div>
        );
    }

    const rows = visibleNotifications.map((notification) => (
        <NotificationRow
            key={notification.uuid}
            notification={notification}
            avatar={
                notification.messageUser?.uuid
                    ? avatars[notification.messageUser.uuid] || avatarImage
                    : undefined
            }
            expanded={expandedIds.has(notification.uuid)}
            onToggleExpand={() => {
                setExpandedIds((current) => {
                    const next = new Set(current);
                    if (next.has(notification.uuid)) {
                        next.delete(notification.uuid);
                    } else {
                        next.add(notification.uuid);
                    }
                    return next;
                });
            }}
            onMarkRead={markAsRead}
            onMarkUnread={allowMarkUnread ? markAsUnread : undefined}
            onNavigate={(path) => {
                markAsRead(notification.uuid);
                navigate(normalizeNotificationNavigatePath(path) ?? path);
                setOpenNotification?.(false);
            }}
            translate={t}
        />
    ));

    const paginator =
        homeMessages && notifications.length > pageRows ? (
            <Paginator
                first={first}
                rows={pageRows}
                totalRecords={notifications.length}
                onPageChange={(e) => setFirst(e.first)}
                template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate={t("notification.paginator.report")}
                className="dashly-notification-list__paginator"
            />
        ) : null;

    if (homeMessages) {
        return (
            <div className="dashly-notification-list dashly-notification-list--home-paged">
                <div className="dashly-notification-list__scroll">{rows}</div>
                {paginator}
            </div>
        );
    }

    return <div className="dashly-notification-list">{rows}</div>;
};

interface NotificationPanelProps {
    setOpenNotification: (open: boolean) => void;
    fetchNotificationCount: () => void;
    fetchNotifications: () => void;
    notifications: NotificationItem[];
    readNotifications?: NotificationItem[];
    stopPropagation: (e: React.MouseEvent) => void;
    onNotificationReadChange?: (uuid: string, read: boolean) => void;
}

const Notification: React.FC<NotificationPanelProps> = ({
    setOpenNotification,
    fetchNotificationCount,
    fetchNotifications,
    notifications,
    stopPropagation,
    onNotificationReadChange,
}) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const unreadCount = notifications.filter((n) => !n.read).length;

    return (
        <div className="dashly-notification-panel" onClick={stopPropagation} role="dialog" aria-label={t("notification.title")}>
            <header className="dashly-notification-panel__header">
                <div>
                    <h2 className="dashly-notification-panel__title">{t("notification.title")}</h2>
                    {unreadCount > 0 && (
                        <span className="dashly-notification-panel__badge">
                            {t("notification.panel.unreadBadge", { count: unreadCount })}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    className="dashly-notification-panel__close"
                    aria-label={t("notification.close")}
                    onClick={() => setOpenNotification(false)}
                >
                    <i className="pi pi-times" />
                </button>
            </header>

            <div className="dashly-notification-panel__body">
                <NotificationList
                    notifications={notifications}
                    navigate={navigate}
                    setOpenNotification={setOpenNotification}
                    fetchNotificationCount={fetchNotificationCount}
                    fetchNotifications={fetchNotifications}
                    onNotificationReadChange={onNotificationReadChange}
                />
            </div>

            <footer className="dashly-notification-panel__footer">
                <Button
                    type="button"
                    label={t("notification.viewAll")}
                    icon="pi pi-external-link"
                    iconPos="right"
                    text
                    className="dashly-notification-panel__view-all"
                    onClick={() => {
                        navigate("/notifications");
                        setOpenNotification(false);
                    }}
                />
            </footer>
        </div>
    );
};

export default Notification;
